import { BUTTON_STYLE_CONFIG } from '@frontend/components/src/components/forms/button/buttonStyleConfig';
import {
  getBaseStyleObjectFromStyleConfig,
  getStyleObjectFromStyleInterpolation,
  type StyleFunctionProps,
  type StyleObjectMulti,
} from '@frontend/components/src/theme/styleConfigHelpers';

import { EMPTY_PARTS } from './emptyParts';
import type {
  EmptyColorScheme,
  EmptyPart,
  EmptySize,
  EmptyStyleConfig,
  EmptyVariant,
} from './types';

const baseStyle: EmptyStyleConfig['baseStyle'] = ({ theme, colorMode }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: 'feature.placeholder',
  },
  headline: {
    fontWeight: 'medium',
    color: 'feature.primary',
    fontFamily: 'default',
    textAlign: 'center',
  },
  description: {
    fontWeight: 'default',
    color: 'feature.secondary',
    fontFamily: 'default',
    textAlign: 'center',
  },
  ctaButton: {
    ...getBaseStyleObjectFromStyleConfig(BUTTON_STYLE_CONFIG, {
      theme,
      variant: 'primary',
      colorScheme: 'default',
      colorMode,
    }),
    mt: 1.5,
  },
});

const getBaseSizeStyles = (
  size: EmptySize,
  { theme, colorMode }: StyleFunctionProps<EmptyVariant, EmptyColorScheme>,
): StyleObjectMulti<EmptyPart> => {
  const buttonStyles = getStyleObjectFromStyleInterpolation(
    BUTTON_STYLE_CONFIG.sizes[size],
    {
      theme,
      variant: 'primary',
      colorScheme: 'default',
      colorMode,
    },
  );

  const fontStyles = {
    fontSize: size,
    lineHeight: size,
  };

  return {
    headline: fontStyles,
    description: fontStyles,
    ctaButton: buttonStyles,
  };
};

const sizes: EmptyStyleConfig['sizes'] = {
  sm: (styleFunctionProps) => {
    const base = getBaseSizeStyles('sm', styleFunctionProps);

    return {
      ...base,
      container: {
        ...base.container,
        gap: 0,
        fontSize: '2rem',
        px: 1,
      },
    };
  },
  md: (styleFunctionProps) => {
    const base = getBaseSizeStyles('md', styleFunctionProps);

    return {
      ...base,
      container: {
        ...base.container,
        gap: 0,
        fontSize: '2.5rem',
        px: 1.5,
      },
    };
  },
  lg: (styleFunctionProps) => {
    const base = getBaseSizeStyles('lg', styleFunctionProps);

    return {
      ...base,
      container: {
        ...base.container,
        gap: 0.5,
        fontSize: '3rem',
        px: 2,
      },
    };
  },
};

const defaultProps = {
  size: 'md',
} as const;

export const EMPTY_STYLE_CONFIG: EmptyStyleConfig = {
  parts: EMPTY_PARTS,
  baseStyle,
  sizes,
  defaultProps,
};
